import { Injectable, inject } from '@angular/core';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { Interval } from 'date-fns';
import { TimeBlockCost } from '../../project-edit/edit-project.types';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { DefaultDateFormat } from '../../../../../shared/localization/formats';
import { ProjectExpenditureEntryModel } from '../../../../../core/models/project/project-expenditure-entry.model';
import { pairwise } from '../../../../../shared/functions/array-functions';
import { CostRateEntryService } from '../../project-edit/manage-project/project-calculation-form/services/cost-rate-entry.service';
import { CostRateEntryModel } from '../../../../../core/models/user/cost-rate-entry.model';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { take } from 'rxjs/operators';
import { UserService } from '../../../../user/services/user.service';
import { assert } from '../../../../../core/assert/assert';

@Injectable({
  providedIn: 'root',
})
export class TimeBlockCostCalculationService {
  private readonly costRateEntryService = inject(CostRateEntryService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly userService = inject(UserService);

  public timeBlockCosts(
    timeBlocksWithTask: readonly TimeBlockWithTask[],
    excludeOverlappingTime: boolean,
    timeRange: Interval = {
      start: null,
      end: null,
    },
  ): TimeBlockCost[] {
    const timeBlockComponents = timeBlocksWithTask.map((tb) => tb.timeBlock);
    const splitTimeBlockMapPerDay =
      this.timeBlockDurationCalculationService.createTimeBlockMapPerDay(
        timeBlockComponents,
        excludeOverlappingTime,
        timeRange,
      );

    const timeBlockCosts: TimeBlockCost[] = [];
    for (const [dateString, timeBlocks] of splitTimeBlockMapPerDay) {
      for (const timeBlock of timeBlocks) {
        const date = DateTimeHelper.parse(dateString, DefaultDateFormat, new Date());
        const userId = +(timeBlock.timeBlockModel as TimeBlockProjectModel).userId;
        assert(userId !== null);

        const costRateEntryModel = this.getCostOnDateForTimeBlock(userId, date);
        const timeBlockCost = {
          timeBlockWithTask: {
            timeBlock,
            task: timeBlocksWithTask.find((tb) => tb.timeBlock.id === timeBlock.id).task,
          },
          costRate: costRateEntryModel.rate,
        };
        timeBlockCosts.push(timeBlockCost);
      }
    }
    return timeBlockCosts;
  }

  private getCostOnDateForTimeBlock(userId: number, date: Date): CostRateEntryModel {
    const user$ = this.userService.getUserEnsured(userId);
    let costRateEntry: CostRateEntryModel = null;

    user$.pipe(take(1)).subscribe((user) => {
      // todo martin sort
      const costRateEntries = [...user.costRateEntries];

      if (costRateEntries.length === 0) {
        return null;
      }
      // add dummy entry at beginning and the end
      costRateEntries.push(
        this.costRateEntryService.createCostEntry(costRateEntries[0].rate, null, userId),
      );
      costRateEntries.unshift(
        this.costRateEntryService.createCostEntry(
          costRateEntries[costRateEntries.length - 1].rate,
          null,
          userId,
        ),
      );

      const intervals = pairwise<boolean>(
        costRateEntries,
        (first: ProjectExpenditureEntryModel, second: ProjectExpenditureEntryModel) => {
          return DateTimeHelper.isDateInOpenInterval(date, {
            start: first.startingFrom ?? null,
            end: second.startingFrom ?? null,
          });
        },
      );

      const entryIndex = intervals.indexOf(true) === 0 ? 1 : intervals.lastIndexOf(true);
      costRateEntry = costRateEntries[entryIndex];
    });
    return costRateEntry;
  }
}
